/**
 * CollapsibleFilterBar - 通用紧凑筛选栏组件
 *
 * 交互模式：摘要常驻（1行 ~40px）+ 顶部 Drawer 展开完整筛选表单
 *
 * 设计原则：
 * - 声明式 API：通过 summaryItems 配置摘要标签，通过 children 传入完整筛选表单
 * - 零侵入：不修改内部 Filter 组件的接口和逻辑，仅包装展示层
 * - 样式隔离：所有样式使用 .cfb- 命名空间，Drawer 通过 rootClassName 隔离
 *
 * summaryItems 格式：
 * [
 *   { label: '状态', value: '计划/飞行中+2', fullValue?: '...', visible?: boolean, hidden?: boolean },
 *   { label: '供应商', value: 'ABC/XYZ' },
 *   ...
 * ]
 * - visible: false → 完全排除（不计数、不显示、不入 tooltip）
 * - hidden: true → 不显示标签，但仍计入筛选总数和 tooltip（适用于外层已有独立控件的筛选项）
 * - value: 显示在 Tag 中的简短摘要
 * - fullValue (可选): 显示在 Tooltip 中的完整信息，不提供时回退到 value
 *
 * 通用的值格式化工具函数（推荐在页面层使用）：
 * - formatArraySummary(items, labelMap, maxShow): 数组值摘要，少量展示名称，多时显示数量
 */

import React, { useState, useMemo, useCallback } from "react";
import { Button, Badge, Drawer, Divider, Tag, Tooltip } from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import "./CollapsibleFilterBar.css";

/**
 * 通用的数组值摘要格式化
 *
 * 场景：多选筛选字段（供应商、状态、费用类型等）需要在摘要栏展示
 * 策略：
 *   - 数量 <= maxShow 时：展示各项的标签文本
 *   - 数量 > maxShow 时：展示前 maxShow 个 + "+N"
 *
 * @param {string[]} items - 值数组
 * @param {Object|Function} labelResolver - 标签映射对象或查找函数 (value) => label
 * @param {number} maxShow - 最多展示几项的完整文本，默认 2
 * @param {string} unit - 超出时的计量单位，默认 "个"
 * @returns {string} 格式化后的摘要文本
 */
export const formatArraySummary = (items, labelResolver, maxShow = 2, unit = "个") => {
    if (!items || items.length === 0) return "";

    const resolveLabel = typeof labelResolver === "function"
        ? labelResolver
        : (v) => (labelResolver && labelResolver[v]) || v;

    if (items.length <= maxShow) {
        return items.map((v) => resolveLabel(v)).join("/");
    }

    const shown = items.slice(0, maxShow).map((v) => resolveLabel(v));
    return `${shown.join("/")}+${items.length - maxShow}${unit}`;
};

const CollapsibleFilterBar = ({
    /** 摘要配置项数组: [{ label: string, value: string, fullValue?: string, visible?: boolean }] */
    summaryItems = [],
    /** 常驻快捷控件（ReactNode），显示在摘要栏右侧 */
    quickControls = null,
    /** 完整筛选表单内容（放在 Drawer 中） */
    children,
    /** 重置回调 */
    onReset,
    /** Drawer 标题 */
    drawerTitle = "筛选条件",
    /** 是否显示重置按钮 */
    showReset = true,
}) => {
    const [drawerVisible, setDrawerVisible] = useState(false);
    // resetKey 递增时强制 children 重新挂载，确保 Form 内部状态也被清空
    const [resetKey, setResetKey] = useState(0);

    const handleReset = useCallback(() => {
        setResetKey((k) => k + 1);
        onReset?.();
    }, [onReset]);

    // 计算可见的摘要项（过滤掉 visible=false 和空值）
    const visibleSummaryItems = useMemo(() => {
        return summaryItems.filter((item) => {
            if (item.visible === false) return false;
            if (item.value === undefined || item.value === null || item.value === "") return false;
            return true;
        });
    }, [summaryItems]);

    const activeFilterCount = visibleSummaryItems.length;

    // Tooltip 展示完整筛选条件（优先使用 fullValue）
    const summaryTooltip = useMemo(() => {
        if (visibleSummaryItems.length === 0) return "点击展开筛选条件";
        return visibleSummaryItems.map((item) => `${item.label}: ${item.fullValue || item.value}`).join("\n");
    }, [visibleSummaryItems]);

    return (
        <>
            {/* 紧凑摘要栏 */}
            <div className="cfb-bar">
                {/* 筛选摘要按钮 - 点击展开 Drawer */}
                <Tooltip
                    title={<span style={{ whiteSpace: "pre-line" }}>{summaryTooltip}</span>}
                    placement="bottomLeft"
                >
                    <Button
                        type="text"
                        icon={<FilterOutlined />}
                        onClick={() => setDrawerVisible(true)}
                        size="small"
                        className="cfb-summary-btn"
                    >
                        {visibleSummaryItems.length > 0 ? (
                            <span className="cfb-summary-tags">
                                {visibleSummaryItems.filter(item => !item.hidden).slice(0, 4).map((item, index) => (
                                    <Tag key={index} color="blue">
                                        {item.label}: {item.value}
                                    </Tag>
                                ))}
                                {visibleSummaryItems.filter(item => !item.hidden).length > 4 && (
                                    <Tag>+{visibleSummaryItems.filter(item => !item.hidden).length - 4}</Tag>
                                )}
                            </span>
                        ) : (
                            <span className="cfb-summary-text">筛选</span>
                        )}
                        {activeFilterCount > 0 && (
                            <Badge
                                count={activeFilterCount}
                                size="small"
                                style={{ marginLeft: 6, backgroundColor: "#1890ff" }}
                            />
                        )}
                    </Button>
                </Tooltip>

                {/* 快捷控件区域 */}
                {quickControls && (
                    <>
                        <Divider type="vertical" className="cfb-divider" />
                        <div className="cfb-quick-controls">{quickControls}</div>
                    </>
                )}

                {/* 右侧操作区 */}
                <div className="cfb-actions">
                    {showReset && onReset && activeFilterCount > 0 && (
                        <Button
                            type="link"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={handleReset}
                            className="cfb-reset-btn"
                        >
                            重置
                        </Button>
                    )}
                </div>
            </div>

            {/* 完整筛选抽屉 */}
            <Drawer
                title={drawerTitle}
                placement="top"
                height="auto"
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                rootClassName="cfb-drawer"
                styles={{
                    body: { paddingBottom: 16 },
                }}
            >
                <div key={resetKey}>{children}</div>
            </Drawer>
        </>
    );
};

export default CollapsibleFilterBar;
